import {
  Users,
  MessageCircle,
  Gauge,
  AlertTriangle,
  ChevronRight,
  Clock,
  Circle,
} from 'lucide-react';

const CENTRAL_TZ = 'America/Chicago';

const fmtCentral = (iso, opts = {}) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    timeZone: CENTRAL_TZ,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
    ...opts,
  });
};

const SEVERITY_STYLES = {
  Critical: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300 border-red-200 dark:border-red-800',
  High:     'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300 border-orange-200 dark:border-orange-800',
  Medium:   'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border-amber-200 dark:border-amber-800',
  Low:      'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 border-blue-200 dark:border-blue-800',
  Info:     'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700',
};

const statusPill = (status) => {
  if (status === 'Monitoring') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
        <Circle size={8} className="fill-current animate-pulse" />
        Monitoring
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
      <Clock size={10} /> {status}
    </span>
  );
};

export default function WatchCard({ watch, onOpenHistory }) {
  const m = watch.latestMetrics || {};
  const hasHubErr = (m.hubConnExceptions || 0) > 0;

  return (
    <div
      className={`bg-white dark:bg-slate-800 rounded-xl border ${
        hasHubErr
          ? 'border-red-300 dark:border-red-700 ring-2 ring-red-200 dark:ring-red-900/40'
          : 'border-slate-200 dark:border-slate-700'
      }`}
    >
      {/* Header row */}
      <div className="flex items-start justify-between p-5 pb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {watch.tenant}
            </span>
            {statusPill(watch.status)}
            {hasHubErr && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">
                <AlertTriangle size={10} /> HubConnections errors
              </span>
            )}
          </div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mt-1 truncate">
            {watch.title || `Event #${watch.tenantEventId}`}
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Scheduled {fmtCentral(watch.eventDateTime)}
            {watch.startedMonitoringAt && (
              <> · Watching since {fmtCentral(watch.startedMonitoringAt)}</>
            )}
          </p>
        </div>
        <button
          onClick={onOpenHistory}
          className="inline-flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium shrink-0"
        >
          History <ChevronRight size={14} />
        </button>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 px-5 pb-4">
        <Metric icon={Users} label="Connections" value={m.connectionCount ?? '—'} sub={watch.peakConnections ? `peak ${watch.peakConnections}` : null} />
        <Metric icon={MessageCircle} label="Msgs/min" value={m.messageCount ?? '—'} />
        <Metric icon={Gauge} label="Server Load" value={m.serverLoadPct != null ? `${Number(m.serverLoadPct).toFixed(1)}%` : '—'} />
        <Metric icon={AlertTriangle} label="Alerts" value={watch.totalAlerts || 0} accent={watch.totalAlerts > 0 ? 'amber' : 'default'} />
      </div>

      {/* Recent alerts */}
      {watch.recentAlerts && watch.recentAlerts.length > 0 && (
        <div className="border-t border-slate-200 dark:border-slate-700 px-5 py-3 space-y-2">
          {watch.recentAlerts.slice(0, 3).map((a) => (
            <div
              key={a.id}
              className={`flex items-start gap-2 text-xs rounded-lg border px-3 py-2 ${SEVERITY_STYLES[a.severity] || SEVERITY_STYLES.Info}`}
            >
              <span className="font-semibold shrink-0">{a.severity}</span>
              <div className="min-w-0 flex-1">
                <p className="font-medium">{a.ruleName}</p>
                <p className="opacity-90">{a.message}</p>
                {a.suggestion && (
                  <p className="opacity-80 mt-0.5 italic">Suggestion: {a.suggestion}</p>
                )}
              </div>
              <span className="text-[10px] opacity-60 shrink-0">{fmtCentral(a.firedAt, { year: undefined })}</span>
            </div>
          ))}
          {watch.recentAlerts.length > 3 && (
            <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
              +{watch.recentAlerts.length - 3} more — view history
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function Metric({ icon: Icon, label, value, sub, accent }) {
  const accents = {
    default: 'text-slate-600 dark:text-slate-300',
    amber: 'text-amber-600 dark:text-amber-400',
  };
  return (
    <div className="flex items-start gap-2">
      <Icon className={`mt-1 ${accents[accent || 'default']}`} size={14} />
      <div>
        <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</p>
        <p className="text-lg font-semibold text-slate-900 dark:text-slate-100 leading-tight">{value}</p>
        {sub && <p className="text-[11px] text-slate-400 dark:text-slate-500">{sub}</p>}
      </div>
    </div>
  );
}
